import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Trash2, Bookmark, FolderOpen, PlayCircle, Loader2, FileCheck2, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";

export default function SavedPapers({ embedded = false } = {}) {
  const [papers, setPapers] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => {
    setLoadError(null);
    return api.get("/papers")
      .then((r) => setPapers(r.data))
      .catch((e) => setLoadError(errMsg(e)));
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/papers/${id}`);
      toast.success("Paper deleted");
      load();
    } catch (e) {
      toast.error("Could not delete paper");
    }
  };

  const shown = (papers || []).filter((p) => filter === "all" || (filter === "bookmarked" && p.bookmarked) || (filter === "predicted" && p.predicted));

  const body = (
    <>
      {!embedded && (
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-2">Library</p>
            <h1 className="text-4xl font-bold tracking-tighter">Saved Papers</h1>
          </div>
          <Link to="/submissions" data-testid="saved-papers-to-submissions" className="text-xs text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1.5 mt-2">
            View submissions <FileCheck2 className="h-3.5 w-3.5" />
          </Link>
        </div>
      )}

      <div className={embedded ? "flex gap-2" : "mt-6 flex gap-2"}>
        {["all", "bookmarked", "predicted"].map((f) => (
          <Button key={f} variant={filter === f ? "default" : "outline"} size="sm" data-testid={`filter-${f}-btn`}
            onClick={() => setFilter(f)} className="rounded-full capitalize">{f}</Button>
        ))}
      </div>

      {loadError && (
        <div className="mt-10 rounded-3xl border border-border bg-card p-8 text-center" data-testid="saved-papers-error">
          <p className="text-sm font-semibold text-destructive mb-2">Couldn&apos;t load your saved papers</p>
          <p className="text-xs text-muted-foreground mb-6">{loadError}</p>
          <Button data-testid="saved-papers-retry-btn" onClick={load} className="rounded-full glow-btn" size="sm">Retry</Button>
        </div>
      )}

      {!loadError && papers === null && (
        <div className="mt-10 flex items-center justify-center py-16" data-testid="saved-papers-loading">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      )}

      {!loadError && papers && shown.length === 0 && (
        <div className="mt-10 rounded-3xl border border-dashed border-border p-16 text-center" data-testid="saved-papers-empty">
          <FolderOpen className="h-6 w-6 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground text-sm mb-4">
            {filter === "all"
              ? "No papers yet."
              : `No ${filter} papers yet.`}
          </p>
          <Button asChild size="sm" className="rounded-full glow-btn" data-testid="saved-papers-generate-cta">
            <Link to="/generate">Generate your first paper →</Link>
          </Button>
        </div>
      )}

      <div className={`${embedded ? "mt-4" : "mt-8"} grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5`}>
        {shown.map((p) => {
          const ls = p.latest_submission;
          const topicsSummary = (p.topics && p.topics.length)
            ? (p.topics.length <= 2 ? p.topics.join(" + ") : `${p.topics.slice(0, 2).join(" + ")} +${p.topics.length - 2}`)
            : "Mixed topics";
          const when = new Date(p.created_at);
          const whenLabel = when.toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
          return (
            <div key={p.id} className="card-lift rounded-3xl border border-border bg-card p-5 flex flex-col" data-testid={`paper-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="h-9 w-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <FileText className="h-4 w-4 text-primary" />
                  </div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground truncate">
                    {p.exam_board} {p.qualification} · Paper {p.paper_number}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {p.bookmarked && <Bookmark className="h-4 w-4 fill-primary text-primary" />}
                  {ls && (
                    <Link to={`/submissions/${ls.id}`} data-testid={`paper-sat-badge-${p.id}`}
                      title="Open latest attempt"
                      className="h-6 w-6 rounded-full bg-emerald-500/15 text-emerald-500 flex items-center justify-center hover:bg-emerald-500/25 transition-colors">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                    </Link>
                  )}
                </div>
              </div>

              <h3 className="mt-4 font-semibold tracking-tight leading-snug text-base" data-testid={`paper-title-${p.id}`}>
                {topicsSummary} · {p.total_marks} marks
              </h3>

              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {p.predicted && <Badge className="bg-purple-500/15 text-purple-400 hover:bg-purple-500/15 text-[10px]">Predicted</Badge>}
                <Badge variant="secondary" className="text-[10px]">{p.num_questions} questions</Badge>
                <Badge variant="secondary" className="text-[10px]">{p.difficulty}</Badge>
              </div>

              <p className="mt-3 text-[11px] text-muted-foreground" data-testid={`paper-timestamp-${p.id}`}>{whenLabel}</p>

              {ls ? (
                <Link to={`/submissions/${ls.id}`} data-testid={`paper-latest-attempt-${p.id}`}
                  className="mt-3 inline-flex items-center gap-1.5 text-[11px] text-emerald-500 hover:underline">
                  <CheckCircle2 className="h-3 w-3" />
                  Sat — {ls.total_awarded}/{ls.total_available}{ls.estimated_grade ? ` (${ls.estimated_grade})` : ""}
                </Link>
              ) : (
                <p className="mt-3 text-[11px] text-muted-foreground italic" data-testid={`paper-not-sat-${p.id}`}>Not yet sat</p>
              )}

              <div className="mt-4 flex gap-2 mt-auto pt-3">
                <Button asChild size="sm" className="rounded-full flex-1" data-testid={`open-paper-${p.id}`}>
                  <Link to={`/papers/${p.id}`}>Open</Link>
                </Button>
                <Button asChild variant="outline" size="sm" className="rounded-full flex-1" data-testid={`sit-paper-${p.id}`}>
                  <Link to={`/exam/${p.id}`}><PlayCircle className="h-3.5 w-3.5 mr-1" /> Sit Exam</Link>
                </Button>
                <Button variant="ghost" size="icon" data-testid={`delete-paper-${p.id}`} onClick={() => remove(p.id)} className="rounded-full text-muted-foreground hover:text-destructive">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );

  if (embedded) return <div data-testid="saved-papers-page">{body}</div>;
  return <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="saved-papers-page">{body}</div>;
}
